import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, CreditCard, CheckCircle, AlertCircle, Building2, Clock, Lock } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Company Information
const COMPANY_INFO = {
  name: 'Apollo Shades',
  address: '1 Brooklyn Navy Yard, Building 33, Suite 201B',
  city: 'Brooklyn',
  state: 'NY',
  zip: '11205',
  phone: '(212) 209-0654',
  email: 'info@apolloshades.com',
};

export const InvoicePaymentPage = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receipt, setReceipt] = useState(null);

  // Check for payment success redirect
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      setPaymentSuccess(true);
      // Verify the payment
      verifyPayment(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice();
    }
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/public`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      setError(error.response?.data?.detail || 'Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/invoices/${invoiceId}/verify-payment`, {
        session_id: sessionId
      });
      if (response.data.success) {
        toast.success('Payment successful!');
        setReceipt(response.data.receipt_sent ? response.data.receipt_to : null);
        fetchInvoice(); // Refresh invoice to show updated status
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
    }
  };

  const handlePayNow = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/invoices/${invoiceId}/create-payment`, {
        origin_url: window.location.origin,
        success_url: `${window.location.origin}/pay/invoice/${invoiceId}?success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pay/invoice/${invoiceId}?canceled=true`
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Online card payment is temporarily unavailable. Please contact us for payment options.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="container-lg text-center max-w-md mx-auto">
            <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-6" />
            <h1 className="text-2xl font-serif mb-4">Invoice Not Found</h1>
            <p className="text-neutral-600 mb-6">{error}</p>
            <Button onClick={() => window.location.href = '/'} className="btn-primary">
              Return Home
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (paymentSuccess || (invoice?.status === 'paid' && !Number(invoice?.refunded_amount || 0))) {
    return (
      <Layout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="container-lg max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-neutral-200 rounded-xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-serif mb-4">Payment Complete</h1>
              <p className="text-neutral-600 mb-2">
                Thank you for your payment!
              </p>
              <p className="text-neutral-500 text-sm mb-8">
                Invoice #{invoice?.invoice_number} has been marked as paid.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <p className="text-sm text-green-700">Amount Paid</p>
                    <p className="text-2xl font-mono font-bold text-green-800">
                      {formatPrice(invoice?.payments?.length ? invoice.payments[invoice.payments.length - 1].amount : (invoice?.total || 0))}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-neutral-500 mb-6" data-testid="invoice-payment-receipt-message">
                {receipt ? `A payment confirmation has been sent to ${receipt}.` : 'Your payment was received successfully.'}
              </p>

              <Button onClick={() => window.location.href = '/'} className="btn-primary">
                Return to the website
              </Button>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  const balanceDue = invoice?.balance_due ?? (invoice?.total - (invoice?.amount_paid || 0));

  return (
    <Layout>
      <div className="min-h-screen pt-24 pb-16 bg-neutral-50">
        <div className="container-lg max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <span className="label-overline text-amber-700">Invoice Payment</span>
              <h1 className="mt-4 text-neutral-900">Pay Invoice {invoice?.invoice_number}</h1>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Invoice Details */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                  {/* Company Header */}
                  <div className="bg-neutral-900 text-white p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-2xl font-serif">{COMPANY_INFO.name}</h2>
                        <p className="text-neutral-400 mt-1">INVOICE</p>
                      </div>
                      <div className="text-right text-sm">
                        <p>{COMPANY_INFO.address}</p>
                        <p>{COMPANY_INFO.city}, {COMPANY_INFO.state} {COMPANY_INFO.zip}</p>
                        <p className="mt-2">{COMPANY_INFO.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Info */}
                  <div className="bg-blue-50 border-x border-blue-200 px-6 py-3 flex justify-between text-sm">
                    <span><strong>Invoice #:</strong> {invoice?.invoice_number}</span>
                    <span><strong>Date:</strong> {new Date(invoice?.created_at).toLocaleDateString()}</span>
                    <span><strong>Due:</strong> {invoice?.due_date ? new Date(invoice?.due_date).toLocaleDateString() : 'Upon Receipt'}</span>
                  </div>

                  {/* Bill To */}
                  <div className="p-6 border-b border-neutral-200">
                    <h3 className="text-xs font-medium text-neutral-500 uppercase mb-3">Bill To</h3>
                    <div className="bg-neutral-50 p-4 rounded-lg inline-block">
                      <p className="font-medium">{invoice?.customer_name}</p>
                      {invoice?.company_name && <p className="text-neutral-600">{invoice?.company_name}</p>}
                      <p className="text-neutral-600 mt-2">{invoice?.customer_email}</p>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="p-6">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left text-xs font-medium text-neutral-500 uppercase">
                          <th className="pb-3">Description</th>
                          <th className="pb-3 text-center">Qty</th>
                          <th className="pb-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice?.items?.map((item, idx) => (
                          <tr key={idx} className="border-b border-neutral-100">
                            <td className="py-3">
                              <p className="font-medium">{item.config?.room_label || item.config?.product_type?.replace('-', ' ')}</p>
                              <p className="text-sm text-neutral-500">
                                {item.config?.width}" × {item.config?.height}" • {item.config?.fabric_material}
                              </p>
                            </td>
                            <td className="py-3 text-center">{item.quantity}</td>
                            <td className="py-3 text-right font-mono">{formatPrice(item.config?.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Totals */}
                    <div className="mt-6 flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-500">Subtotal</span>
                          <span className="font-mono">{formatPrice(invoice?.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-500">Tax</span>
                          <span className="font-mono">{formatPrice(invoice?.tax)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-medium pt-2 border-t">
                          <span>Total</span>
                          <span className="font-mono">{formatPrice(invoice?.total)}</span>
                        </div>
                        {(invoice?.gross_paid ?? invoice?.amount_paid ?? 0) > 0 && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Paid</span>
                            <span className="font-mono">-{formatPrice(invoice?.gross_paid ?? invoice?.amount_paid)}</span>
                          </div>
                        )}
                        {Number(invoice?.refunded_amount || 0) > 0 && (
                          <>
                            <div className="flex justify-between text-sm text-neutral-600" data-testid="pay-refunded-line">
                              <span>Refunded</span>
                              <span className="font-mono">+{formatPrice(invoice?.refunded_amount)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>Remaining paid</span>
                              <span className="font-mono">{formatPrice(invoice?.amount_paid)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Card */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-neutral-200 rounded-xl p-6 sticky top-24">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                    Payment Summary
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Invoice Total</span>
                      <span className="font-mono">{formatPrice(invoice?.total)}</span>
                    </div>
                    {Number(invoice?.refunded_amount || 0) > 0 ? (
                      <>
                        <div className="flex justify-between text-green-600"><span>Amount Paid</span><span className="font-mono" data-testid="pay-gross-paid">-{formatPrice(invoice?.gross_paid)}</span></div>
                        <div className="flex justify-between text-neutral-600"><span>Refunded</span><span className="font-mono" data-testid="pay-refunded">{formatPrice(invoice?.refunded_amount)}</span></div>
                        <div className="flex justify-between text-neutral-600"><span>Remaining Paid</span><span className="font-mono" data-testid="pay-net-paid">{formatPrice(invoice?.amount_paid)}</span></div>
                      </>
                    ) : (invoice?.amount_paid || 0) > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Previously Paid</span>
                        <span className="font-mono">-{formatPrice(invoice?.amount_paid)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-3 border-t">
                      <span>Amount Due</span>
                      <span className="font-mono text-amber-700">{formatPrice(balanceDue)}</span>
                    </div>
                  </div>

                  {balanceDue > 0 ? (
                    <>
                      <Button
                        onClick={handlePayNow}
                        disabled={processing}
                        className="w-full btn-primary mb-4"
                        data-testid="pay-now-button"
                      >
                        {processing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay {formatPrice(balanceDue)} Now
                          </>
                        )}
                      </Button>

                      <div className="flex items-center gap-2 text-xs text-neutral-500 justify-center">
                        <Lock className="w-3 h-3" />
                        Secure payment powered by Stripe
                      </div>

                      <div className="mt-6 pt-4 border-t border-neutral-200">
                        <p className="text-xs text-neutral-500 text-center">
                          Questions about this invoice?<br />
                          Contact us at {COMPANY_INFO.phone}
                        </p>
                      </div>
                    </>
                  ) : Number(invoice?.refunded_amount || 0) > 0 ? (
                    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-center" data-testid="pay-refund-state">
                      <p className="font-medium text-neutral-800">{invoice?.payment_status === 'refunded' ? 'REFUNDED' : 'PARTIALLY REFUNDED'}</p>
                      <p className="text-sm text-neutral-600 mt-1">{formatPrice(invoice?.refunded_amount)} was refunded to the original payment method. Nothing is due.</p>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="font-medium text-green-800">PAID IN FULL</p>
                      <p className="text-sm text-green-600 mt-1">
                        {invoice?.paid_date && `Paid on ${new Date(invoice.paid_date).toLocaleDateString()}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default InvoicePaymentPage;
